import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type Region = 'US' | 'CA';
export type Language = 'en' | 'fr';

interface LocaleState {
  language: Language;
  region: Region;
  fullLocale: string;
}

const determineRegion = (): Region => {
  const isClient = typeof window !== 'undefined';
  const isCanadaHost = isClient
    ? window.location.host.includes('.ca') ||
      window.location.host.includes('.app')
    : false;

  let region: Region = isCanadaHost ? 'CA' : 'US';

  let regionSet = false;
  if (isClient && navigator?.languages) {
    navigator.languages.forEach(l => {
      if (regionSet) return;

      const lower = l.toLowerCase();
      if (lower.includes('us')) {
        region = 'US';
        regionSet = true;
      } else if (lower.includes('ca')) {
        region = 'CA';
        regionSet = true;
      }
    });
  }

  return region;
};

const determineLanguage = (): Language => {
  if (typeof window === 'undefined') return 'en';

  const storedLang = localStorage.getItem('language');

  if (storedLang === 'en' || storedLang === 'fr') {
    return storedLang as Language;
  }

  const browserHasFr = navigator?.languages?.some(l => l.includes('fr'));
  return browserHasFr ? 'fr' : 'en';
};

const initialRegion = determineRegion();
const initialLanguage = determineLanguage();

const initialState: LocaleState = {
  region: initialRegion,
  language: initialLanguage,
  fullLocale: `${initialLanguage}-${initialRegion}`,
};

export const localeSlice = createSlice({
  name: 'locale',
  initialState,
  selectors: {
    selectCurrentLocale: (state: LocaleState) => state.fullLocale,
    selectCurrentLanguage: (state: LocaleState) => state.language,
    selectCurrentRegion: (state: LocaleState) => state.region,
  },
  reducers: {
    setLocaleState: (
      state,
      action: PayloadAction<{ lang: Language; region: Region }>,
    ) => {
      state.language = action.payload.lang;
      state.region = action.payload.region;
      state.fullLocale = `${action.payload.lang}-${action.payload.region}`;
    },
    setLanguageInternal: (state, action: PayloadAction<Language>) => {
      state.language = action.payload;
      state.fullLocale = `${action.payload}-${state.region}`;
    },
  },
});

export const { setLocaleState, setLanguageInternal } = localeSlice.actions;
export const {
  selectCurrentLocale,
  selectCurrentLanguage,
  selectCurrentRegion,
} = localeSlice.selectors;
